import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ChartBar,
  FileText,
  FileCsv,
  FileXls,
  FileDoc,
  Download,
  ArrowsClockwise,
  CalendarBlank,
  CurrencyDollar,
  Users,
  NavigationArrow,
  Warning,
  Receipt,
} from '@phosphor-icons/react';
import api from '../services/api';
import { formatCurrency, formatDateTime } from '../lib/utils';

const REPORT_TYPES = [
  { id: 'revenue', name: 'Revenue Report', icon: CurrencyDollar, description: 'Weekly revenue by status and day', color: 'text-emerald-600' },
  { id: 'claims-status', name: 'Claims Status', icon: FileText, description: 'All claims by status', color: 'text-blue-600' },
  { id: 'evv-summary', name: 'EVV Summary', icon: NavigationArrow, description: 'Visits by status and employee', color: 'text-indigo-600' },
  { id: 'payments', name: 'Payments Received', icon: Receipt, description: '835 remittance payments', color: 'text-green-600' },
  { id: 'edi-errors', name: 'EDI Errors', icon: Warning, description: 'Error trends and analysis', color: 'text-amber-600' },
  { id: 'employee-productivity', name: 'Employee Productivity', icon: Users, description: 'Staff hours and performance', color: 'text-purple-600' },
];

const EXPORT_FORMATS = [
  { id: 'csv', name: 'CSV', icon: FileCsv },
  { id: 'xlsx', name: 'Excel', icon: FileXls },
  { id: 'docx', name: 'Word', icon: FileDoc },
];

const WEEK_OPTIONS = [
  { value: '0', label: 'This Week' },
  { value: '-1', label: 'Last Week' },
  { value: '-2', label: '2 Weeks Ago' },
  { value: '-3', label: '3 Weeks Ago' },
  { value: '-4', label: '4 Weeks Ago' },
];

const ReportsPage = () => {
  const [selectedReport, setSelectedReport] = useState('revenue');
  const [weekOffset, setWeekOffset] = useState('0');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/${selectedReport}`, {
        params: { week_offset: weekOffset }
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [selectedReport, weekOffset]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await api.get(`/reports/${selectedReport}`, {
        params: { week_offset: weekOffset, format },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedReport}_report.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const currentReportType = REPORT_TYPES.find(r => r.id === selectedReport);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Reports
        </h1>
        <p className="text-slate-500 mt-1">
          Weekly billing reports with export options
        </p>
      </div>

      {/* Report Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {REPORT_TYPES.map((report) => {
          const Icon = report.icon;
          const isSelected = selectedReport === report.id;
          return (
            <Card
              key={report.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50' : 'border-slate-200'
              }`}
              onClick={() => setSelectedReport(report.id)}
              data-testid={`report-card-${report.id}`}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex flex-col items-center text-center">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                    <Icon size={24} className={isSelected ? 'text-indigo-600' : report.color} />
                  </div>
                  <p className={`text-sm font-medium mt-2 ${isSelected ? 'text-indigo-900' : 'text-slate-700'}`}>
                    {report.name}
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Controls */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CalendarBlank size={20} className="text-slate-500" />
                <Select value={weekOffset} onValueChange={setWeekOffset}>
                  <SelectTrigger className="w-40" data-testid="week-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEK_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={fetchReport} disabled={loading}>
                <ArrowsClockwise size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 mr-2">Export:</span>
              {EXPORT_FORMATS.map((format) => {
                const Icon = format.icon;
                return (
                  <Button
                    key={format.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport(format.id)}
                    disabled={exporting !== null}
                    data-testid={`export-${format.id}`}
                  >
                    {exporting === format.id ? (
                      <ArrowsClockwise size={16} className="animate-spin" />
                    ) : (
                      <>
                        <Icon size={16} className="mr-1" />
                        {format.name}
                      </>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reportData ? (
        <div className="space-y-6">
          {/* Period Info */}
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold text-slate-900">
                    {currentReportType?.name}
                  </p>
                  <p className="text-sm text-slate-500">
                    {reportData.period?.week_label}
                  </p>
                </div>
                <Badge variant="outline" className="bg-white">
                  Generated: {formatDateTime(reportData.generated_at)}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(reportData.summary || {}).map(([key, value]) => (
              <Card key={key} className="border-slate-200">
                <CardContent className="pt-4">
                  <p className="text-sm text-slate-500 capitalize">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-2xl font-bold text-slate-900">
                    {typeof value === 'number' && key.includes('amount') || key.includes('paid') || key.includes('billed') || key.includes('revenue')
                      ? formatCurrency(value)
                      : typeof value === 'number' && key.includes('rate')
                      ? `${value}%`
                      : value}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Data Tables */}
          {reportData.by_status && Object.keys(reportData.by_status).length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">By Status</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      {reportData.by_status[Object.keys(reportData.by_status)[0]]?.billed !== undefined && (
                        <TableHead className="text-right">Billed</TableHead>
                      )}
                      {reportData.by_status[Object.keys(reportData.by_status)[0]]?.hours !== undefined && (
                        <TableHead className="text-right">Hours</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(reportData.by_status).map(([status, data]) => (
                      <TableRow key={status}>
                        <TableCell>
                          <Badge variant="outline">{status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{data.count || data}</TableCell>
                        {data.billed !== undefined && (
                          <TableCell className="text-right">{formatCurrency(data.billed)}</TableCell>
                        )}
                        {data.hours !== undefined && (
                          <TableCell className="text-right">{data.hours.toFixed(1)} hrs</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {reportData.by_employee && Object.keys(reportData.by_employee).length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">By Employee</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Patients</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(reportData.by_employee).slice(0, 10).map(([name, data]) => (
                      <TableRow key={name}>
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell className="text-right">{data.visits}</TableCell>
                        <TableCell className="text-right">{data.hours.toFixed(1)} hrs</TableCell>
                        <TableCell className="text-right">{data.patients_served}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {reportData.status_breakdown && Object.keys(reportData.status_breakdown).length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">This Week</TableHead>
                      <TableHead className="text-right">Billed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(reportData.status_breakdown).map(([status, data]) => (
                      <TableRow key={status}>
                        <TableCell>
                          <Badge variant="outline">{status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{data.total}</TableCell>
                        <TableCell className="text-right">{data.this_week}</TableCell>
                        <TableCell className="text-right">{formatCurrency(data.billed)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <ChartBar size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">Select a report type to view data</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
